import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Minus, Plus, ShoppingBag, ImageOff } from "lucide-react";
import { toast } from "sonner";
import { api, imgUrl } from "@/lib/api";
import { euro, fmtWeight, categoryPath } from "@/lib/shop";
import { ShareButton } from "@/components/ShareButton";
import { HeartButton } from "@/components/HeartButton";
import { StockAlertButton } from "@/components/StockAlertButton";
import { RecentlyViewed, trackView } from "@/components/RecentlyViewed";
import { ProductBadges } from "@/components/Badges";
import { Spinner } from "@/components/ProtectedRoute";
import { useCart } from "@/context/CartContext";
import { useShopSettings } from "@/context/ShopSettingsContext";
import { TikTokShopButton } from "@/components/TikTokShopButton";

const DEFAULT_TITLE = "MC Anime - Games - TCG & Shop";

function useProductSeo(p) {
  useEffect(() => {
    if (!p) return undefined;
    const meta = document.querySelector('meta[name="description"]');
    const prevDesc = meta?.getAttribute("content");
    document.title = `${p.name} | ${DEFAULT_TITLE}`;
    meta?.setAttribute("content", (p.description || `${p.name} - ${categoryPath(p).join(" › ")}`).slice(0, 160));
    return () => { document.title = DEFAULT_TITLE; if (prevDesc) meta.setAttribute("content", prevDesc); };
  }, [p]);
}

const Gallery = ({ images, name }) => {
  const [i, setI] = useState(0);
  if (!images?.length) {
    return <div className="card-mc grid aspect-square place-items-center text-muted-foreground"><ImageOff className="h-10 w-10" /></div>;
  }
  return (
    <div>
      <div className="card-mc aspect-square overflow-hidden">
        <img data-testid="product-main-image" src={imgUrl(images[i])} alt={name} className="h-full w-full object-contain p-3" />
      </div>
      {images.length > 1 && (
        <div className="mt-3 flex gap-2 overflow-x-auto">
          {images.map((u, k) => (
            <button key={u} data-testid={`product-thumb-${k}`} onClick={() => setI(k)}
              className={`h-16 w-16 shrink-0 overflow-hidden rounded-lg border-2 bg-white ${k === i ? "border-[#0b0b0f]" : "border-border"}`}>
              <img src={imgUrl(u)} alt="" className="h-full w-full object-contain" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default function ProductPage() {
  const { id } = useParams();
  const [p, setP] = useState(null);
  const [error, setError] = useState(false);
  const [qty, setQty] = useState(1);
  const { add } = useCart();
  const { showcase_mode } = useShopSettings();
  useProductSeo(p);

  useEffect(() => {
    api.get(`/products/${id}`).then(({ data }) => { setP(data); trackView(data.id); }).catch(() => setError(true));
    setQty(1);
  }, [id]);

  if (error) {
    return (
      <div data-testid="product-not-found" className="mx-auto max-w-md px-4 py-24 text-center">
        <h1 className="text-2xl font-extrabold">Articolo non disponibile</h1>
        <Link to="/" className="btn-mc mt-6">Torna al catalogo</Link>
      </div>
    );
  }
  if (!p) return <Spinner />;

  const dims = [p.length_cm, p.width_cm, p.height_cm].map((v) => v.toLocaleString("it-IT")).join(" × ");
  return (
    <div className="mx-auto max-w-6xl px-4 pt-5 sm:px-6 lg:px-8">
      <Link to="/" data-testid="product-back-link" className="inline-flex items-center gap-1 text-sm font-semibold hover:underline">
        <ArrowLeft className="h-4 w-4" /> Catalogo
      </Link>
      <div className="mt-4 grid gap-8 md:grid-cols-2">
        <Gallery images={p.images} name={p.name} />
        <div className="rise">
          <ProductBadges p={p} />
          {p.category && (
            <div data-testid="product-category-path" className="mt-2 text-xs font-semibold text-muted-foreground">{categoryPath(p).join(" ➔ ")}</div>
          )}
          <h1 data-testid="product-name" className="mt-3 text-2xl font-extrabold leading-tight sm:text-3xl">{p.name}</h1>
          {!showcase_mode && <div data-testid="product-price" className="mt-3 font-display text-3xl font-black">{euro(p.price)}</div>}
          <div data-testid="product-stock" className={`mt-1 text-sm ${p.quantity > 0 ? "text-muted-foreground" : "font-bold text-red-600"}`}>
            {p.quantity > 0 ? `Disponibili: ${p.quantity}` : "Esaurito"}
          </div>

          <div className="mt-6 flex flex-wrap items-center gap-3">
            {showcase_mode && p.quantity > 0 ? (
              <TikTokShopButton product={p} size="lg" className="flex-1" testId="product-tiktok-button" placement="product_page" />
            ) : p.quantity > 0 ? (
            <>
            <div className="flex items-center rounded-full border-2 border-[#0b0b0f] bg-white">
              <button data-testid="qty-decrease" className="p-2.5" onClick={() => setQty((q) => Math.max(1, q - 1))}><Minus className="h-4 w-4" /></button>
              <span data-testid="qty-value" className="w-8 text-center font-bold">{qty}</span>
              <button data-testid="qty-increase" className="p-2.5" onClick={() => setQty((q) => Math.min(p.quantity, q + 1))}><Plus className="h-4 w-4" /></button>
            </div>
            <button
              data-testid="product-add-to-cart"
              className="btn-mc flex-1 !py-3"
              onClick={() => { add(p, qty); toast.success("Aggiunto al carrello"); }}
            >
              <ShoppingBag className="h-5 w-5" /> Aggiungi al carrello
            </button>
            </>
            ) : (
              <>
                <button data-testid="product-add-to-cart" className="btn-mc flex-1 !py-3" disabled>
                  <ShoppingBag className="h-5 w-5" /> Esaurito
                </button>
                <StockAlertButton key={p.id} productId={p.id} />
              </>
            )}
            <HeartButton id={p.id} className="h-12 w-12 shrink-0" />
            <ShareButton name={p.name} />
          </div>

          <dl className="mt-8 grid grid-cols-2 gap-3 text-sm">
            <div className="card-mc p-3"><dt className="text-xs font-bold uppercase text-muted-foreground">Peso</dt><dd data-testid="product-weight" className="font-bold">{fmtWeight(p.weight_g)}</dd></div>
            <div className="card-mc p-3"><dt className="text-xs font-bold uppercase text-muted-foreground">Misure</dt><dd data-testid="product-dimensions" className="font-bold">{dims} cm</dd></div>
          </dl>
          {(p.set_name || p.card_number || p.rarity || p.year) && (
            <div data-testid="product-set-details" className="card-mc mt-3 p-3 text-sm">
              {[["Espansione", p.set_name], ["Numero", p.card_number], ["Rarità", p.rarity], ["Anno", p.year]]
                .filter(([, v]) => v)
                .map(([k, v], i) => <span key={k}>{i > 0 && " · "}{k}: <span className="font-bold">{v}</span></span>)}
            </div>
          )}
          {p.condition === "Usato" && (
            <div data-testid="product-used-details" className="card-mc mt-3 p-3 text-sm">
              <span className="font-bold">{p.used_type}</span>
              {p.platform && <> · Piattaforma: <span className="font-bold">{p.platform}</span></>}
              <span className="ml-1 text-muted-foreground">· Articolo di seconda mano</span>
            </div>
          )}
          {p.description && (
            <p data-testid="product-description" className="mt-6 whitespace-pre-line text-sm leading-relaxed text-slate-700">{p.description}</p>
          )}
        </div>
      </div>
      <RecentlyViewed currentId={p.id} />
    </div>
  );
}
